// packages/components/src/admin/finance/BalanceDisplay.tsx

import React from 'react';
import { useCurrentBalance, useBalanceHistory } from '@dopomogai/agent-kit';
import { Card, Spinner, Alert, Button, Icon, StatCard } from '@dopomogai/ui';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Helper to get currency symbol from ISO code
const getCurrencySymbol = (code: number): string => {
    if (code === 980) return '₴';
    if (code === 840) return '$';
    if (code === 978) return '€';
    return '';
};

/**
 * A specialized component to render the API error message cleanly.
 * It attempts to parse JSON, but falls back to showing raw text for XML or other formats.
 */
const ApiErrorDisplay: React.FC<{ error: Error }> = ({ error }) => {
    let detail = error.message;

    // The backend formats the error message as "Monobank API returned an error: <actual response>"
    const prefix = "Monobank API returned an error: ";
    if (detail.startsWith(prefix)) {
        detail = detail.substring(prefix.length);
    }
    
    // Check if the detail is a JSON string
    try {
        const parsedJson = JSON.parse(detail);
        if (parsedJson.errorDescription) {
            detail = parsedJson.errorDescription;
        }
    } catch (e) {
        // It's not JSON (like the XML we're seeing), so we'll just display it as is.
    }

    return (
        <Alert
            type="error"
            title="Could not fetch balance"
            message={detail}
        />
    );
};


export const BalanceDisplay: React.FC = () => {
    const { balanceData, isLoading: isBalanceLoading, error: balanceError, refresh } = useCurrentBalance();
    const { history, isLoading: isHistoryLoading, error: historyError } = useBalanceHistory();

    // Prepare data for the chart: filter for corporate accounts and format the date
    const formattedHistory = history
        .filter(item => item.account_type === 'fop') 
        .map(item => ({
            ...item,
            date: new Date(item.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        }));

    return (
        <div className="space-y-8">
            <Card
                title="Current Account Balances"
                actionButton={
                    <Button
                        text="Refresh"
                        variant="secondary"
                        size="sm"
                        icon={isBalanceLoading ? <Spinner size="sm" /> : <Icon name="RefreshCw" />}
                        onClick={() => refresh()}
                        disabled={isBalanceLoading}
                    />
                }
            >
                {isBalanceLoading && (
                    <div className="p-8 flex justify-center items-center">
                        <Spinner text="Fetching latest balance..." />
                    </div>
                )}

                {balanceError && <ApiErrorDisplay error={balanceError} />}
                
                {balanceData && !isBalanceLoading && (
                     balanceData.accounts.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {balanceData.accounts.map(acc => (
                                <StatCard
                                    key={acc.id}
                                    title="Corporate Account"
                                    value={`${getCurrencySymbol(acc.currencyCode)}${acc.balance.toFixed(2)}`}
                                    description={`IBAN: ${acc.id}`}
                                    icon="Building"
                                />
                            ))}
                        </div>
                     ) : (
                        <p className="text-slate-500 p-4">No corporate accounts found for the provided token.</p>
                     )
                )}
            </Card>

            <Card title="Corporate Account Balance History (UAH)">
                 {isHistoryLoading && (
                    <div className="p-8 flex justify-center items-center">
                        <Spinner text="Loading history..." />
                    </div>
                 )}

                 {historyError && (
                    <Alert type="error" title="Could not load history" message={historyError.message} />
                 )}

                 {!isHistoryLoading && formattedHistory.length === 0 && (
                    <p className="text-slate-500 p-4 text-center">
                        No historical data available yet. The chart will populate after the first daily snapshot runs successfully.
                    </p>
                 )}

                 {formattedHistory.length > 0 && (
                     <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <LineChart data={formattedHistory} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                <XAxis dataKey="date" stroke="#666" fontSize={12} />
                                <YAxis yAxisId="left" stroke="#666" fontSize={12} />
                                <Tooltip formatter={(value: number) => `${value.toFixed(2)} ₴`} />
                                <Legend />
                                <Line yAxisId="left" type="monotone" dataKey="balance" stroke="#10b981" name="Balance" activeDot={{ r: 8 }}/>
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                 )}
            </Card>
        </div>
    );
};